import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from 'react';
import { Badge, Button, useToast, Tabs, TabsBar, TabsPanel } from '@vertesia/ui/core';
import { useUITranslation } from '../../../i18n/index.js';
import { CheckCircleIcon, ClipboardCopyIcon, DownloadCloudIcon, FileTextIcon, Loader2Icon, LayoutListIcon, XCircleIcon, XIcon, } from 'lucide-react';
import { FileProcessingStatus } from '@vertesia/common';
import { useUserSession } from '@vertesia/ui/session';
import InlineSlidingPlanPanel from './ModernAgentOutput/InlineSlidingPlanPanel';
import { getConversationUrl } from './ModernAgentOutput/utils.js';
import { DocumentPanel } from './DocumentPanel.js';
import { ArtifactsTab } from './ArtifactsTab.js';
function UploadedDocumentsTab({ files }) {
    const { t } = useUITranslation();
    const filesArray = useMemo(() => {
        return files ? Array.from(files.values()) : [];
    }, [files]);
    const getStatusIcon = (status) => {
        switch (status) {
            case FileProcessingStatus.UPLOADING:
            case FileProcessingStatus.PROCESSING:
                return _jsx(Loader2Icon, { className: "size-4 animate-spin text-info" });
            case FileProcessingStatus.READY:
                return _jsx(CheckCircleIcon, { className: "size-4 text-success" });
            case FileProcessingStatus.ERROR:
                return _jsx(XCircleIcon, { className: "size-4 text-destructive" });
            default:
                return _jsx(FileTextIcon, { className: "size-4 text-muted" });
        }
    };
    const getStatusBadge = (status) => {
        switch (status) {
            case FileProcessingStatus.UPLOADING:
                return _jsx(Badge, { variant: "info", children: t('agent.uploading') });
            case FileProcessingStatus.PROCESSING:
                return _jsx(Badge, { variant: "info", children: t('agent.processing') });
            case FileProcessingStatus.READY:
                return _jsx(Badge, { variant: "success", children: t('agent.ready') });
            case FileProcessingStatus.ERROR:
                return _jsx(Badge, { variant: "destructive", children: t('agent.error') });
            default:
                return null;
        }
    };
    return (_jsx("div", { className: "p-3", children: filesArray.length === 0 ? (_jsx("div", { className: "text-sm text-muted text-center py-8", children: t('agent.noFilesUploadedYet') })) : (_jsx("div", { className: "space-y-2", children: filesArray.map((file) => (_jsxs("div", { className: "flex items-start gap-2 p-2 border border-muted rounded-md", children: [_jsx("div", { className: "mt-0.5", children: getStatusIcon(file.status) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsx("span", { className: "text-sm font-medium truncate", children: file.name }), getStatusBadge(file.status)] }), file.error && (_jsx("div", { className: "text-xs text-destructive mt-1", children: file.error }))] })] }, file.id))) })) }));
}
function WorkstreamsTab({ workstreams, messages: _messages }) {
    const { t } = useUITranslation();
    const { client } = useUserSession();
    const toast = useToast();
    const copyRunId = useCallback((runId) => {
        navigator.clipboard.writeText(runId);
        toast({ status: 'success', title: t('agent.runIdCopied'), duration: 2000 });
    }, [toast]);
    const downloadConversation = useCallback(async (runId) => {
        try {
            const url = await getConversationUrl(client, runId);
            if (url)
                window.open(url, '_blank');
        }
        catch {
            toast({ status: 'error', title: t('agent.failedToDownload') });
        }
    }, [client, toast]);
    if (workstreams.length === 0) {
        return (_jsxs("div", { className: "flex flex-col items-center justify-center py-8 text-muted", children: [_jsx(LayoutListIcon, { className: "size-8 mb-2" }), _jsx("span", { className: "text-sm", children: t('agent.noActiveWorkstreams') })] }));
    }
    return (_jsx("div", { className: "p-3 space-y-2", children: workstreams.map((ws) => {
            const isActive = ws.status === 'running' || ws.status === 'canceling';
            const elapsed = Math.round(ws.elapsed_ms / 1000);
            const remaining = Math.round(ws.remaining_ms / 1000);
            const progress = ws.deadline_ms > 0
                ? Math.min(100, Math.round((ws.elapsed_ms / ws.deadline_ms) * 100))
                : 0;
            const statusBadge = ws.status === 'running'
                ? _jsx(Badge, { variant: "info", children: ws.phase || 'running' })
                : ws.status === 'canceling'
                    ? _jsx(Badge, { variant: "attention", children: "canceling" })
                    : ws.status === 'completed'
                        ? _jsx(Badge, { variant: "done", children: t('agent.completed') })
                        : _jsx(Badge, { variant: "destructive", children: t('agent.canceled') });
            return (_jsxs("div", { className: "p-3 border rounded-md space-y-2", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { className: "text-sm font-medium truncate", children: ws.workstream_id }), statusBadge] }), isActive && (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-full h-1.5 bg-muted rounded-full overflow-hidden", children: _jsx("div", { className: "h-full bg-info rounded-full transition-all duration-500", style: { width: `${progress}%` } }) }), _jsxs("div", { className: "flex justify-between text-xs text-muted", children: [_jsx("span", { children: t('agent.elapsed', { seconds: elapsed }) }), _jsx("span", { children: t('agent.remaining', { seconds: remaining }) })] })] })), ws.child_workflow_run_id && (_jsxs("div", { className: "flex gap-1 pt-1 border-t border-muted", children: [_jsxs(Button, { variant: "ghost", size: "sm", className: "text-xs h-7 px-2 text-muted hover:text-foreground", onClick: () => copyRunId(ws.child_workflow_run_id), children: [_jsx(ClipboardCopyIcon, { className: "size-3 mr-1" }), t('agent.copyRunId')] }), _jsxs(Button, { variant: "ghost", size: "sm", className: "text-xs h-7 px-2 text-muted hover:text-foreground", onClick: () => downloadConversation(ws.child_workflow_run_id), children: [_jsx(DownloadCloudIcon, { className: "size-3 mr-1" }), t('agent.download')] })] }))] }, ws.launch_id));
        }) }));
}
function AgentRightPanelComponent({ 
// Plan
plan, workstreamStatus, plans = [], activePlanIndex = 0, onChangePlan, showPlan, 
// Workstreams
activeWorkstreams = [], messages = [], hideWorkstreams = false, 
// Documents
openDocuments = [], activeDocumentId, onSelectDocument, onCloseDocument, onUpdateDocumentTitle, docRefreshKey = 0, runId, 
// Uploads
processingFiles, 
// Artifacts
showArtifacts = false, artifactRefreshKey = 0, 
// Payload
payloadContent, 
// Conversation
conversationContent, 
// Panel
onClose, defaultTab, activeTab: activeTabProp, onTabChange, }) {
    const { t } = useUITranslation();
    const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || 'plan');
    const activeTab = activeTabProp ?? internalActiveTab;
    const handleTabChange = (name) => {
        setInternalActiveTab(name);
        onTabChange?.(name);
    };
    // Determine which tabs have content (for badges/indicators)
    const hasWorkstreams = !hideWorkstreams && activeWorkstreams.length > 0;
    const hasDocuments = openDocuments.length > 0;
    const hasUploads = processingFiles ? processingFiles.size > 0 : false;
    const hasPlan = showPlan && plan;
    const conversationTab = {
        name: 'conversation',
        label: 'Conversation',
        content: conversationContent ? _jsx("div", { className: "flex flex-col h-full min-h-0", children: conversationContent }) : null,
        is_allowed: !!conversationContent,
    };
    const tabs = [
        ...(conversationContent ? [conversationTab] : []),
        {
            name: 'plan',
            label: hasPlan
                ? _jsxs("span", { className: "flex items-center gap-1", children: [t('agent.plan'), " ", _jsx("span", { className: "inline-block w-1.5 h-1.5 rounded-full bg-info" })] })
                : t('agent.plan'),
            content: plan ? (_jsx(InlineSlidingPlanPanel, { plan: plan, workstreamStatus: workstreamStatus || new Map(), isOpen: true, onClose: onClose, plans: plans, activePlanIndex: activePlanIndex, onChangePlan: onChangePlan })) : (_jsx("div", { className: "flex flex-col items-center justify-center py-8 text-muted", children: _jsx("span", { className: "text-sm", children: t('agent.noPlanAvailable') }) })),
            is_allowed: true,
        },
        {
            name: 'workstreams',
            label: hasWorkstreams
                ? _jsxs("span", { className: "flex items-center gap-1", children: [t('agent.workstreams'), " ", _jsx("span", { className: "inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] rounded-full bg-info text-info", children: activeWorkstreams.length })] })
                : t('agent.workstreams'),
            content: _jsx(WorkstreamsTab, { workstreams: activeWorkstreams, messages: messages, runId: runId }),
            is_allowed: !hideWorkstreams,
        },
        {
            name: 'documents',
            label: hasDocuments
                ? _jsxs("span", { className: "flex items-center gap-1", children: [t('agent.documents'), " ", _jsx("span", { className: "inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] rounded-full bg-info text-info", children: openDocuments.length })] })
                : t('agent.documents'),
            content: openDocuments.length > 0 && onSelectDocument && onCloseDocument ? (_jsx(DocumentPanel, { isOpen: true, documents: openDocuments, activeDocumentId: activeDocumentId ?? null, onSelectDocument: onSelectDocument, onCloseDocument: onCloseDocument, onUpdateDocumentTitle: onUpdateDocumentTitle, refreshKey: docRefreshKey, runId: runId })) : (_jsxs("div", { className: "flex flex-col items-center justify-center py-8 text-muted", children: [_jsx(FileTextIcon, { className: "size-8 mb-2" }), _jsx("span", { className: "text-sm", children: t('agent.noDocumentsOpen') })] })),
            is_allowed: true,
        },
        {
            name: 'uploads',
            label: hasUploads
                ? _jsxs("span", { className: "flex items-center gap-1", children: [t('agent.uploads'), " ", _jsx("span", { className: "inline-block w-1.5 h-1.5 rounded-full bg-info" })] })
                : t('agent.uploads'),
            content: _jsx(UploadedDocumentsTab, { files: processingFiles }),
            is_allowed: true,
        },
        {
            name: 'artifacts',
            label: t('agent.artifacts'),
            content: _jsx(ArtifactsTab, { runId: runId, refreshKey: artifactRefreshKey }),
            is_allowed: showArtifacts,
        },
        {
            name: 'payload',
            label: t('agent.payload'),
            content: payloadContent ? _jsx("div", { className: "overflow-y-auto", children: payloadContent }) : null,
            is_allowed: !!payloadContent,
        },
    ];
    return (_jsxs(Tabs, { tabs: tabs, current: activeTab, onTabChange: handleTabChange, fullHeight: true, className: "px-0", children: [_jsxs("div", { className: "flex items-center border-b shrink-0 px-1", children: [_jsx("div", { className: "flex-1 overflow-x-auto", children: _jsx(TabsBar, { className: "border-b-0 mb-0 min-w-max" }) }), !conversationContent && (_jsx(Button, { variant: "ghost", size: "sm", className: "shrink-0 ml-1", onClick: onClose, children: _jsx(XIcon, { className: "size-4" }) }))] }), _jsx(TabsPanel, { className: "pt-0" })] }));
}
export const AgentRightPanel = React.memo(AgentRightPanelComponent);
//# sourceMappingURL=AgentRightPanel.js.map